export type GroupRefundIntent = {
  isGroup: boolean;
  type: "refund" | "upgrade";
  session: number;
  remainingSession: number;
};

export type GroupPaymentInput = {
  type: "refund" | "upgrade";
  session: number;
  amount: number;
};

export type RefundPreviewInput = {
  refundableAmount: number;
  remainingSession: number;
  refundAmount: number;
  refundSession: number;
};

export type RefundPreview = {
  refundableAmount: number;
  remainingAmountAfterRefund: number;
  remainingSessionAfterRefund: number;
};

const nonNegativeNumber = (value: number): number => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? Math.max(0, numericValue) : 0;
};

/** 입력 중인 환불 회차·금액을 기준으로 확정 전 단순 예상 잔여값을 만든다. */
export const getRefundPreview = ({
  refundableAmount,
  remainingSession,
  refundAmount,
  refundSession,
}: RefundPreviewInput): RefundPreview => {
  const currentRefundableAmount = nonNegativeNumber(refundableAmount);
  const currentRemainingSession = nonNegativeNumber(remainingSession);

  return {
    refundableAmount: currentRefundableAmount,
    remainingAmountAfterRefund: Math.max(
      0,
      currentRefundableAmount - nonNegativeNumber(refundAmount),
    ),
    remainingSessionAfterRefund: Math.max(
      0,
      currentRemainingSession - nonNegativeNumber(refundSession),
    ),
  };
};

/** PT·그룹 환불/업그레이드에 공통 적용하는 숫자 계약 사전 검증. */
export const getPaymentInputError = ({
  type,
  session,
  amount,
}: GroupPaymentInput): string | null => {
  const numericSession = Number(session);
  const numericAmount = Number(amount);
  const isValidSession =
    Number.isSafeInteger(numericSession) &&
    (type === "refund" ? numericSession >= 0 : numericSession > 0);
  const isValidAmount = Number.isFinite(numericAmount) && numericAmount >= 0;
  const hasRefundValue =
    type !== "refund" || numericSession > 0 || numericAmount > 0;
  if (isValidSession && isValidAmount && hasRefundValue) return null;
  return type === "refund"
    ? "환불 회차는 0 이상의 정수, 환불 금액은 0 이상이어야 하며 둘 중 하나는 0보다 커야 합니다."
    : "추가 회차는 0보다 큰 정수이고 추가 결제 금액은 0 이상이어야 합니다.";
};

/** 기존 호출부 호환 alias. */
export const getGroupPaymentInputError = getPaymentInputError;

/** 원매출 환불은 금액이 필수이고, 반복 부분 환불의 잔액만 처리할 때 회차 0을 허용한다. */
export const getOriginalSaleRefundInputError = (
  session: number,
  amount: number,
): string | null =>
  Number.isSafeInteger(Number(session)) &&
  Number(session) >= 0 &&
  Number.isSafeInteger(Number(amount)) &&
  Number(amount) > 0
    ? null
    : "원매출 환불 회차는 0 이상의 정수, 환불 금액은 0보다 큰 정수로 입력해주세요.";

/**
 * 그룹 수강권의 미래 예약 일괄 취소까지 동반하는 전액 환불 플래그 판정.
 * 0 >= 0 비교가 true가 되는 JavaScript 특성 때문에 양쪽 모두 양수인 경우만 허용한다.
 */
export const shouldMarkGroupFullRefund = ({
  isGroup,
  type,
  session,
  remainingSession,
}: GroupRefundIntent): boolean => {
  const refundSession = Number(session);
  const availableSession = Number(remainingSession);
  return (
    isGroup &&
    type === "refund" &&
    Number.isFinite(refundSession) &&
    Number.isFinite(availableSession) &&
    refundSession > 0 &&
    availableSession > 0 &&
    refundSession >= availableSession
  );
};
